import * as Sentry from '@sentry/react';
import { NetworkError } from './error';

// Window globals (app_env / sentryProKey / sentryProId) are already declared in lib/otel.ts
// window 全局变量（app_env / sentryProKey / sentryProId）已在 lib/otel.ts 中声明，此处不重复声明

type ErrorLevel = 'info' | 'error';

interface SentryInfo {
    extra?: any;
    tags: Record<string, string>;
    report: boolean;
    level: ErrorLevel;
}

const handleError = (error: any) => {
    const info: SentryInfo = {
        extra: undefined,
        tags: {},
        report: true,
        level: 'error',
    };
    if (!error || typeof error !== 'object') return info;
    if (error?.extra) {
        info.extra = error.extra || {};
    }
    if (error?.tags) {
        info.tags = error.tags;
    }
    if (error?.type) {
        info.tags.type = error.type;
    }
    if (typeof error?.report === 'boolean') {
        info.report = error.report;
    }
    if (error?.level) {
        info.level = error.level || 'error';
    }
    return info;
};

const runSentry = async () => {
    if (process.env.NODE_ENV !== 'development') {
        const { app_env, sentryProId, sentryProKey } = window || {};
        if (!sentryProId || !sentryProKey) {
            return;
        }
        let locationInfo: any = {};
        try {
            const response = await fetch('https://frontapi.finpoints.tech/');
            if (response.ok) {
                const apiRes = await response.json();
                if (apiRes?.code == 0) {
                    locationInfo = apiRes?.data;
                }
            } else {
                console.error('Failed to get region info');
            }
        } catch (error) {
            console.error('Error:', error);
        }
        Sentry.init({
            dsn: `${window.location.protocol}//${sentryProKey}@${window.location.host}/sentry/${sentryProId}`,
            integrations: [new Sentry.BrowserTracing()],
            release: process.env.RELEASE,
            tracesSampleRate: 1.0,
            environment: app_env,
            initialScope: {
                tags: {
                    city: locationInfo?.city || undefined,
                    country: locationInfo?.iso_country_code || undefined,
                    region: locationInfo?.region || undefined,
                },
            },
            beforeBreadcrumb: (breadcrumb) => {
                if (breadcrumb.category === 'console' && ['log', 'warning'].includes(breadcrumb.level as string)) {
                    return null;
                }

                return breadcrumb;
            },
            beforeSend: (event, hint) => {
                const originalException = hint?.originalException;
                const { tags, extra, report, level } = handleError(originalException || {});
                if (!report) {
                    return null;
                }

                // 全量上报：不再按错误码静音任何 NetworkError（含会话/限流 2800/423 等）。
                // 业务码/机制码的过滤交由 API 周报口径处理，Sentry 保留全量原始错误流。

                // Dedupe: HTTP utility already reports API errors via sentryReport (as NetworkError).
                // Drop the duplicate unhandledrejection event to avoid <unknown> noise.
                // 去重：HTTP 工具层已通过 sentryReport 上报 NetworkError，丢弃 onunhandledrejection 的重复事件
                const mechanism = event.exception?.values?.[0]?.mechanism;
                if (
                    mechanism?.type === 'onunhandledrejection' &&
                    originalException &&
                    typeof originalException === 'object' &&
                    'code' in originalException
                ) {
                    return null;
                }

                // Drop content-less errors. @sentry/browser's extractMessage() returns the
                // literal 'No error message' when a captured exception has an empty message —
                // these carry no actionable signal (no message, usually no useful stack).
                // 丢弃无 message 的空内容错误：Sentry extractMessage 对空 message 返回 'No error message'，无排查价值
                if (event.exception?.values?.some((v) => v?.value === 'No error message')) {
                    return null;
                }

                let traceId = extra?.traceId;
                if (!traceId && event.breadcrumbs?.length) {
                    for (let i = event.breadcrumbs.length - 1; i >= 0; i--) {
                        const bc = event.breadcrumbs[i];
                        if (bc.category === 'xhr' || bc.category === 'fetch') {
                            const headerTraceId = (bc.data as any)?.['x-trace-id'];
                            if (headerTraceId) {
                                traceId = headerTraceId;
                                break;
                            }
                        }
                    }
                }

                if (!originalException && hint?.syntheticException?.stack) {
                    event.exception = event.exception || { values: [] };
                    if (!event.exception.values?.length || event.exception.values[0]?.type === '<unknown>') {
                        // Include first stack frame to avoid grouping unrelated errors under the same 'unknown' message
                        // 用首帧区分不同来源的 syntheticException，防止无 message 的错误被错误聚合
                        const firstFrame = hint.syntheticException.stack.split('\n')[1]?.trim() || '';
                        event.fingerprint = [
                            'syntheticException',
                            hint.syntheticException.message || 'unknown',
                            firstFrame,
                        ];
                    }
                }
                event.tags = {
                    ...event.tags,
                    traceId,
                    uuid: sessionStorage.getItem('uuid') || undefined,
                    route: window.location.pathname,
                    ...tags,
                };
                event.extra = {
                    ...event.extra,
                    ...extra,
                };
                event.level = level || 'error';
                return event;
            },
        });

        // Register after Sentry.init() to guarantee events are captured
        // 在 Sentry.init() 之后注册，确保未捕获的 rejection 不会因初始化未完成而丢失
        window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
            const { reason } = event;
            if (!reason) return;
            // Skip API errors — already reported as NetworkError by sentryReport
            // 跳过 HTTP 工具层的 API 错误，避免与 sentryReport 重复上报
            if (typeof reason === 'object' && 'code' in reason) return;
            const err = reason instanceof Error ? reason : new Error(String(reason));
            Sentry.withScope((scope) => {
                scope.setTag('source', 'global-unhandledrejection');
                scope.setTag('uuid', sessionStorage.getItem('uuid') ?? undefined);
                scope.setTag('route', window.location.pathname);
                scope.setExtra('originalReason', typeof reason === 'object' ? JSON.stringify(reason) : String(reason));
                Sentry.captureException(err);
            });
        });
    }
};

export const sentryReport = async (request: any, err: any, response: Response) => {
    if (Sentry && Sentry.captureException && typeof Sentry?.captureException === 'function') {
        Sentry.captureException(
            new NetworkError({
                request,
                err,
                response,
            }),
        );
    }
};

export default runSentry;
